import React, { useState } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { InfoIcon } from "lucide-react";

export default function PolygonComparisonMetricsCard({
  metrics,
  decision,
  decisionIcon, // e.g. dentalIcons.decision
}) {
  const [showDetails, setShowDetails] = useState(false);

  if (!metrics) {
    return null; // or a placeholder
  }

  return (
    <div className="bg-[#DFF3FA] border border-[#E2F7FF] p-1 rounded-[8px]">
      <Tooltip.Provider delayDuration={150}>
        {/* Main metrics */}
        <div className="flex items-center w-full justify-between py-1">
          <div className="flex items-center gap-1">
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  className="shrink-0 leading-none mt-[1px]"
                >
                  <InfoIcon className="w-3 h-3 text-[#20C3FF]" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="left"
                  align="start"
                  sideOffset={8}
                  className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-xs text-black z-[9999]"
                >
                  {metrics?.area?.tooltip}
                  <Tooltip.Arrow className="fill-white" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
            <span className="text-[#383838] font-semibold">Area Δ</span>
          </div>

          <div
            className="mx-2 grow"
            style={{
              height: 2,
              backgroundImage:
                "linear-gradient(to right, #C4C6C6 0 50%, transparent 50% 100%)",
              backgroundSize: "calc(100% / 20) 100%",
              backgroundRepeat: "repeat-x",
            }}
          />
          <p className="text-[#383838] font-bold">
            {metrics?.area?.displayPct}
          </p>
        </div>

        <div className="flex items-center w-full justify-between py-1">
          <div className="flex items-center gap-1">
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  className="shrink-0 leading-none mt-[1px]"
                >
                  <InfoIcon className="w-3 h-3 text-[#20C3FF]" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="left"
                  align="start"
                  sideOffset={8}
                  className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-xs text-black z-[9999]"
                >
                  {metrics?.perimeter?.tooltip}
                  <Tooltip.Arrow className="fill-white" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
            <span className="text-[#383838] font-semibold">Perimeter Δ</span>
          </div>

          <div
            className="mx-2 grow"
            style={{
              height: 2,
              backgroundImage:
                "linear-gradient(to right, #C4C6C6 0 50%, transparent 50% 100%)",
              backgroundSize: "calc(100% / 15) 100%",
              backgroundRepeat: "repeat-x",
            }}
          />
          <p className="text-[#383838] font-bold">
            {metrics?.perimeter?.displayPct}
          </p>
        </div>

        <div className="flex items-center w-full justify-between py-1">
          <div className="flex items-center gap-1">
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  className="shrink-0 leading-none mt-[1px]"
                >
                  <InfoIcon className="w-3 h-3 text-[#20C3FF]" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="left"
                  align="start"
                  sideOffset={8}
                  className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-xs text-black z-[9999]"
                >
                  {metrics?.matchIndexTooltip}
                  <Tooltip.Arrow className="fill-white" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
            <span className="text-[#383838] font-semibold">Match Index</span>
          </div>

          <div
            className="mx-2 grow"
            style={{
              height: 2,
              backgroundImage:
                "linear-gradient(to right, #C4C6C6 0 50%, transparent 50% 100%)",
              backgroundSize: "calc(100% / 15) 100%",
              backgroundRepeat: "repeat-x",
            }}
          />
          <p className="text-[#383838] font-bold">
            {metrics?.matchIndexDisplay}
          </p>
        </div>

        <div className="flex items-center w-full gap-2 py-1">
          <div className="flex items-center gap-1 whitespace-nowrap">
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  className="shrink-0 leading-none mt-[1px]"
                >
                  <InfoIcon className="w-3 h-3 text-[#20C3FF]" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="left"
                  align="start"
                  sideOffset={8}
                  className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-xs text-black z-[9999]"
                >
                  {metrics?.alignmentQuality?.tooltip}
                  <Tooltip.Arrow className="fill-white" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
            <span className="text-[#383838] font-semibold">
              Alignment Quality
            </span>
          </div>

          <div
            className="mx-2 grow"
            style={{
              height: 2,
              backgroundImage:
                "linear-gradient(to right, #C4C6C6 0 50%, transparent 50% 100%)",
              backgroundSize: "calc(100% / 12) 100%",
              backgroundRepeat: "repeat-x",
            }}
          />
          <p className="text-[#383838] font-bold whitespace-nowrap">
            {metrics?.alignmentQuality?.displayPct}
          </p>
        </div>
      </Tooltip.Provider>

      {/* Recommendation */}
      <div className="bg-white rounded-[8px] p-2 my-1">
        <div className="flex items-center gap-2 text-[#0A9FD4] font-semibold">
          {decisionIcon && (
            <img src={decisionIcon} alt="decision" className="shrink-0" />
          )}
          <p>Recommendation</p>
        </div>
        <div className="flex items-center gap-2 my-1">
          <div
            className="h-4 w-4 rounded-[3px]"
            style={{
              background: decision?.ui,
            }}
          />
          <p className="text-[#000000] font-semibold">{decision?.desc}</p>
        </div>
        <small className="!p-0 block !m-0 text-[12px]">
          {decision?.subText}
        </small>
      </div>

      {/* Show details / advanced metrics */}
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="text-[12px] text-[#0A9FD4] font-semibold flex items-center gap-1"
        >
          {showDetails ? "Hide details" : "Show details"}
          <span>{showDetails ? "▴" : "▾"}</span>
        </button>

        {showDetails && (
          <Tooltip.Provider delayDuration={150}>
            <div className="mt-1 bg-white/60 rounded-[6px] p-2 space-y-1 text-[12px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        className="shrink-0 leading-none mt-[1px]"
                      >
                        <InfoIcon className="w-3 h-3 text-[#20C3FF]" />
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="left"
                        align="start"
                        sideOffset={8}
                        className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-xs text-black z-[9999]"
                      >
                        {metrics?.positionalShift?.tooltip}
                        <Tooltip.Arrow className="fill-white" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                  <span className="text-[#383838] font-semibold">
                    Positional Shift
                  </span>
                </div>

                <span className="text-[#383838] font-semibold">
                  {metrics?.positionalShift?.displayRaw}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        className="shrink-0 leading-none mt-[1px]"
                      >
                        <InfoIcon className="w-3 h-3 text-[#20C3FF]" />
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        side="left"
                        align="start"
                        sideOffset={8}
                        className="w-64 rounded-md bg-white border border-[#EDEDED] shadow-md px-3 py-2 text-xs text-black z-[9999]"
                      >
                        {metrics?.alignmentError?.tooltip}
                        <Tooltip.Arrow className="fill-white" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                  <span className="text-[#383838] font-semibold">
                    Alignment Error
                  </span>
                </div>

                <span className="text-[#383838] font-semibold">
                  {metrics?.alignmentError?.displayRaw}
                  {metrics?.alignmentError?.displayPct &&
                    ` (${metrics?.alignmentError?.displayPct})`}
                </span>
              </div>
            </div>
          </Tooltip.Provider>
        )}
      </div>
    </div>
  );
}
